'use strct';

// external imports
import React, {
    Component
} from 'react';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {connect} from 'react-redux';
import memoize from 'memoize-one';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import {__} from '~/shared/src/utilities/messageCatalog';
import User, {
    GenericPermissionName,
    View
} from '~/shared/src/model/User';
import {DataStructure} from '~/shared/src/utilities/dataStructure';
import {
    ColumnConfig,
    ColumnDefinition,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';
import {BaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {LoadDataParameters} from '~/shared/src/utilities/modules/overview';

// frontend imports
import Overview, {
    Overview as OverviewClass,
    ComponentProps as OverviewComponentProps
} from '~/frontend/src/components/base/Overview';

import {StoreState} from '~/frontend/src/store';
import {getModifiedData} from '~/frontend/src/utilities/dataUtils';
import {
    convertSortersToSortersWithField,
    convertSortersWithFieldToSorters,
    getAllColumns,
    applyAllColumnsToColumns
} from '~/frontend/src/components/base/DataTable/utilities';
import {
    getViews,
    viewSelectorPanelPropsCompare,
    buildPropsFromViewProps
} from '~/frontend/src/components/base/Overview/utilities';
import {ViewOption} from '~/frontend/src/components/base/shared/field/ViewSelectorDropdown';
import {ColumnConfigMenuColumn} from '~/frontend/src/components/base/shared/menu/ColumnConfigMenu';
import {
    ColumnSortMenuSingleColumn,
    ColumnSortMenuColumnSorter
} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {XlsxExport} from '~/frontend/src/components/base/DataTable/XlsxExport';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';

// action imports
import * as PanelManagerActions from '~/frontend/src/actions/panelManager';
import {OpenPanel} from '~/frontend/src/actions/panelManager/openPanel';
import {OpenPanelForItemType} from '~/frontend/src/actions/panelManager/openPanelForItemType';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import saveData, {SaveData} from '~/frontend/src/actions/panel/dataTable/saveData';
import clearSearch, {ClearSearch} from '~/frontend/src/actions/panel/overview/clearSearch';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {SetMessage} from '~/frontend/src/actions/intercom';
import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {
    SetElusiveData,
    ClearElusiveData
} from '~/frontend/src/actions/elusiveData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';


const Actions = Object.assign( {}, PanelManagerActions, PanelActions, { saveOverviewData: saveData, clearSearch }, IntercomActions, ElusiveDataActions );
type SetParam = <K extends keyof OverviewComponentProps>( panelKey : string, paramKey : K, paramValue : OverviewComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof OverviewComponentProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<OverviewComponentProps> ) => SetParamsAction;


export const defaultProps = {

    /**
     * The database schema to filter data and metadata for.
     */
    schema: 'DATA' as Schema,

    /**
     * The column definition of the columns possible to show in the DataTable.
     */
    columnDefinitions: [] as ColumnDefinition[],

    /**
     * The column configuration of the columns to show in the DataTable.
     */
    columns: [] as ColumnConfig[],

    /**
     * The column sorters.
     */
    sorters: [] as ColumnSorter[],

    /**
     * Search result loaded from the backend.
     */
    search: false as boolean,

    /**
     * The current panelProps.
     *
     * Only applicable if <prop>showViewSelector</prop> is set to true.
     */
    viewSelectorPanelProps: {} as PanelProps,

    /**
     * The as configured panelProps.
     *
     * Only applicable if <prop>showViewSelector</prop> is set to true.
     */
    viewSelectorAsConfiguredPanelProps: {} as PanelProps,

    /**
     * Should the save button shown in the toolbar.
     */
    showSaveButton: true as boolean,

    showAddButton: true as boolean,

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount: 0 as number,

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip: '' as string | JSX.Element | ( string | JSX.Element )[],

    /**
     * Should the export Xlsx button be shown in the toolbar.
     */
    showDownloadXlsxButton: true as boolean,

    /**
     * Should the SearchField shown in the toolbar.
     */
    showSearchField: false as boolean,

    /**
     * Show search button - only if no search field is shown.
     */
    showSearchButton: true as boolean,

    /**
     * Show clear search button.
     */
    showClearSearchButton: true as boolean,

    /**
     * Should the ViewSelectorDropdown shown in the toolbar.
     */
    showViewSelector: true as boolean,

    /**
     * Should the view options button shown in the toolbar.
     */
    showViewOptionsButton: true as boolean,

    /**
     * Should the column options button shown in the toolbar.
     */
    showColumnOptionsButton: true as boolean,

    /**
     * Should the column sort button shown in the toolbar.
     */
    showColumnSortButton: true as boolean,

    sortable: true as boolean,

    /**
     * Show reload button.
     */
    showReloadButton: true as boolean,

    showModifiedHint: false as boolean,

    /**
     * Is the overview is editable.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true. Used to enable/disable him.
     */
    editable: false as boolean,

    /**
     * The CSS class name(s) to add to the component.
     */
    className: '' as string
};

export type DefaultProps = typeof defaultProps;

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The key of the overview.
     */
    viewKey : string;

    /**
     * A hash of the loaded overview configuration to detect changes.
     */
    configHash? : string;

    /**
     * The structure of the column data.
     */
    dataStructure? : DataStructure;

    /**
     * The search string to filter data for.
     */
    searchString : string;

    /**
     * Should default values shown for properties.
     */
    showPropertyDefaults : boolean;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames : boolean;

    /**
     * Is the overview in editMode.
     *
     * Only applicable if <prop>editable</prop> is set to true.
     */
    editMode : boolean;

    overviewRef : OverviewClass | null;

    /**
     * The parameters for the route to load the data for exports
     */
    exportLoadDataParameters? : BaseRequestParameters & LoadDataParameters;

    panelConfigIds? : number[];
};

export type ComponentProps = Partial<DefaultProps> & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = {

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveDataState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    messageCatalogTimestamp : number;
};

type ActionProps = {
    // panel manager actions
    openPanel : OpenPanel;
    openPanelForItemType : OpenPanelForItemType;

    // common panel actions
    setParam : SetParam;
    unsetParam : UnsetParam;
    setParams : SetParams;

    // overview actions
    saveOverviewData : SaveData;
    clearSearch : ClearSearch;

    // intercom actions
    setMessage : SetMessage;

    // elusiveData actions
    setElusiveData : SetElusiveData;
    clearElusiveData : ClearElusiveData
}

export type AllProps = DefaultProps & Props & StateProps & ActionProps;


/**
 * Toolbar
 */
class Toolbar extends Component<AllProps> {

    static defaultProps = defaultProps;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.getSelectedViewForViewSelectorDropdown = memoize( this.getSelectedViewForViewSelectorDropdown );
        this.getAllColumns = memoize( this.getAllColumns );
        this.getSortersWithField = memoize( this.getSortersWithField );

        this.onSaveButtonClick = this.onSaveButtonClick.bind( this );
        this.onAddItemButtonClick = this.onAddItemButtonClick.bind( this );
        this.onDownloadXlsxClick = this.onDownloadXlsxClick.bind( this );
        this.onSearchFieldChange = this.onSearchFieldChange.bind( this );
        this.onSearchFieldClear = this.onSearchFieldClear.bind( this );
        this.onSearchFieldSearch = this.onSearchFieldSearch.bind( this );
        this.onClearSearchButtonClick = this.onClearSearchButtonClick.bind( this );
        this.onViewOptionChange = this.onViewOptionChange.bind( this );
        this.onExpandColumnsToHeaderWidth = this.onExpandColumnsToHeaderWidth.bind( this );
        this.onExpandColumnsToScreenWidth = this.onExpandColumnsToScreenWidth.bind( this );
        this.onColumnVisibilityChange = this.onColumnVisibilityChange.bind( this );
        this.onColumnSortChange = this.onColumnSortChange.bind( this );
        this.onViewSelectorSetView = this.onViewSelectorSetView.bind( this );
    }

    /**
     * Handler for Save button click.
     *
     * @private
     */
    onSaveButtonClick() {
        this.props.saveOverviewData( this.props.uid, this.props.schema );
    }

    onAddItemButtonClick( event : React.MouseEvent<HTMLElement> ) {
        const {
                  dataStructure,
                  panelConfigIds
              }                      = this.props,
              rootDataObjectSchema   = ( dataStructure?.item?.objectSchema ?? 'DATA' ).toUpperCase() as Schema,
              rootDataObjectItemType = dataStructure?.item?.objectType;

        if ( rootDataObjectSchema && rootDataObjectItemType ) {
            let panelConfig = user?.getPanelConfigFromIdList( panelConfigIds ?? [] )
                ?? user?.getDefaultPanelPropsForItemType( rootDataObjectSchema, rootDataObjectItemType, event.altKey, !user?.hasGenericPermissionOnItemType( 'nonBlocked', rootDataObjectItemType, rootDataObjectSchema ) ? 'nongeneric' : undefined, [ 'FinderOverview', 'Overview', 'XmlImport' ] );

            if ( panelConfig ) {
                if (
                    panelConfig.panelType !== 'BusinessProcessDesignerView'
                    && panelConfig.panelType !== 'ChartDesignerView'
                    && panelConfig.panelType !== 'FinderOverview'
                    && panelConfig.panelType !== 'FormDesignerView'
                    && panelConfig.panelType !== 'GraphDesignerView'
                    && panelConfig.panelType !== 'MapSearchView'
                    && panelConfig.panelType !== 'MetaModelDesignerView'
                    && panelConfig.panelType !== 'Overview'
                    && panelConfig.panelType !== 'OverviewDesignerView'
                    && panelConfig.panelType !== 'QueryExecute'
                    && panelConfig.panelType !== 'TreeDesignerView'
                    && panelConfig.panelType !== 'TreeTest'
                    && panelConfig.panelType !== 'XmlImport'
                ) {
                    panelConfig = {
                        ...panelConfig,

                        schema: rootDataObjectSchema,

                        createNewItem: {
                            itemTypeId: metaModel[ rootDataObjectSchema ].getItemType( rootDataObjectItemType )!.id,
                            statusId  : metaModel[ rootDataObjectSchema ].getStatus( 'active' )!.id
                        }
                    };
                }

                void this.props.openPanel( panelConfig, undefined, event );
            }
        }
    }

    async onDownloadXlsxClick() {
        // show download requested toast
        this.props.setMessage(
            this.props.uid,
            'toast',
            'xlsxDownloadRequested',
            {
                severity: 'info',
                summary : __( 'YN.base.common.xlsx.downloadRequested.title' ),
                detail  : parse( __( 'YN.base.common.xlsx.downloadRequested.text' ) ),
                closable: true,
                sticky  : true
            }
        );

        // request data
        const fileContent = await XlsxExport.getXlsxFileForOverviewData( this.props );

        if ( fileContent === undefined ) {
            // TODO: better Error-Message?
            this.props.setMessage(
                this.props.uid,
                'toast',
                'xlsxDownloadFailed',
                {
                    severity: 'error',
                    summary : __( 'YN.base.common.xlsx.downloadFailed.title' ),
                    detail  : parse( __( 'YN.base.common.xlsx.downloadFailed.text' ) )
                }
            );
        }
        else {
            const fileName = 'Overview_' + XlsxExport.getWorksheetTitle( this.props );
            fileDownload( fileContent, fileName, 'xlsx' );

            this.props.setMessage(
                this.props.uid,
                'toast',
                'xlsxDownloadComplete',
                {
                    severity: 'success',
                    summary : __( 'YN.base.common.xlsx.downloadComplete.title' ),
                    detail  : parse( __( 'YN.base.common.xlsx.downloadComplete.text' ) )
                }
            );
        }
    }

    /**
     * Handler for SearchField change event
     *
     * @param {String} searchString
     *
     * @private
     */
    onSearchFieldChange( searchString : string ) {
        this.props.setParam( this.props.uid, 'searchString', searchString );
    }

    /**
     * Handler for SearchField clear event
     *
     * @private
     */
    onSearchFieldClear() {
        this.props.setParam( this.props.uid, 'searchString', '' );
    }

    /**
     * Handler for SearchField search event.
     *
     * @private
     */
    onSearchFieldSearch() {
        this.props.setParam( this.props.uid, 'doSearch', true );
        // force reload
        this.props.setElusiveData( this.props.uid, { lastParams: undefined } );
    }

    /**
     * Handler for SearchField clear event
     *
     * @private
     */
    onClearSearchButtonClick() {
        this.props.clearSearch( this.props.uid );
    }

    /**
     * The handler for view option change.
     *
     * @param {string} name
     * @param {Boolean} checked
     *
     * @private
     */
    onViewOptionChange( name : string, checked : boolean ) {
        if ( name === 'editMode' ) {
            this.props.unsetParam( this.props.uid, 'selection' );
        }

        this.props.setParam( this.props.uid, name as keyof OverviewComponentProps, checked );
    }

    /**
     * Handler for reload Button click.
     *
     * @private
     */
    onReloadButtonClick( reloadConfig : boolean ) {
        const reloadFnc = async () => {
            if ( reloadConfig ) {
                await this.props.overviewRef?.loadConfig();
                void this.props.overviewRef?.loadData( undefined, true );
            }
            else {
                void this.props.overviewRef?.loadData( undefined, true );
            }
        };

        void reloadFnc();
    }

    /**
     * The handler for expand columns to header width.
     *
     * @private
     */
    onExpandColumnsToHeaderWidth() {
        this.props.setParam( this.props.uid, 'autoLayout', true );
    }

    /**
     * The handler for expand columns to screen width.
     *
     * @private
     */
    onExpandColumnsToScreenWidth() {
        this.props.setParam( this.props.uid, 'forceFit', true );
    }

    /**
     * Handler for column visibility change.
     *
     * @param {ColumnConfigMenuColumn[]} allColumns
     *
     * @private
     */
    onColumnVisibilityChange( allColumns : ColumnConfigMenuColumn[] ) {
        const {
                  uid,
                  columnDefinitions,
                  columns,
                  sorters,
                  search
              }                                            = this.props,
              newColumns                                   = applyAllColumnsToColumns( allColumns, columnDefinitions, columns, 'last' ),
              newSorters                                   = ( [] as ColumnSorter[] ).concat( sorters ),
              propsToSet : Partial<OverviewComponentProps> = {
                  doSearch: search,
                  columns : newColumns
              };

        // remove sorters for removed columns
        for ( const oldColumn of columns ) {

            // column removed?
            if ( !newColumns.find( column => column.field === oldColumn.field ) ) {
                const sorterIndex = newSorters.findIndex( sorter => sorter.field === oldColumn.field );

                // sorter found?
                if ( sorterIndex !== -1 ) {
                    newSorters.splice( sorterIndex, 1 );

                    if ( !propsToSet.sorters ) {
                        propsToSet.sorters = newSorters;
                    }
                }
            }
        }

        this.props.setParams( uid, propsToSet );
    }

    /**
     * Handler for column sort change.
     *
     * @param {ColumnSortMenuColumnSorter[]} sortersWithField
     *
     * @private
     */
    onColumnSortChange( sortersWithField : ColumnSortMenuColumnSorter[] ) {
        const {
                  uid,
                  search
              }                                            = this.props,
              propsToSet : Partial<OverviewComponentProps> = {
                  doSearch: search,
                  sorters : convertSortersWithFieldToSorters( sortersWithField, this.props.columnDefinitions )
              };

        this.props.setParams( uid, propsToSet );
    }

    /**
     * Handler for viewSelector set view.
     *
     * @param {PanelProps} panelProps
     *
     * @private
     */
    onViewSelectorSetView( panelProps : PanelProps ) {
        if ( panelProps.overviewConfigHash !== undefined && panelProps.overviewConfigHash !== this.props.configHash ) {
            this.props.setMessage?.(
                this.props.uid,
                'toast',
                'configChanged',
                { id: 'showWarn' }
            );
        }

        this.props.setParams( this.props.uid, buildPropsFromViewProps( panelProps ) );
    }

    /**
     * Gets the views from user.
     *
     * @param {string} viewKey
     */
    getViewsForViewSelectorDropdown( viewKey : string ) {
        return getViews( viewKey, this.props.columnDefinitions );
    }

    /**
     * Gets the view from given views which match the given panelProps.
     *
     * @param {string} viewKey
     * @param {Object} [panelProps=undefined]
     */
    getSelectedViewForViewSelectorDropdown( viewKey : string, panelProps? : PanelProps ) {
        const views = this.getViewsForViewSelectorDropdown( viewKey );

        let retVal : View | undefined;

        if ( panelProps ) {
            panelProps = { ...panelProps };
            delete panelProps.editMode;

            for ( const view of views ) {
                const viewPanelProps = { ...view.panelProps };

                if ( viewSelectorPanelPropsCompare( panelProps, viewPanelProps ) ) {
                    retVal = view;
                    break;
                }
            }
        }

        return retVal;
    }

    /**
     * Memoizable wrapper for getAllColumns
     *
     * @private
     */
    getAllColumns( columnDefinitions : ColumnDefinition[], columns : ColumnConfig[], dataStructure : DataStructure, schema : Schema, rawMode : boolean, showAdditionalRawNames : boolean ) : ColumnSortMenuSingleColumn[] {
        return getAllColumns( columnDefinitions, columns, dataStructure, schema, rawMode, showAdditionalRawNames );
    }

    /**
     * Memoizable wrapper for getSortersWithFieldFromSorters
     *
     * @private
     */
    getSortersWithField( sorters : ColumnSorter[] ) : ColumnSortMenuColumnSorter[] {
        return convertSortersToSortersWithField( sorters );
    }

    isInsertable( schema : Schema, itemTypeName : string, user : User | undefined, genericPermission : GenericPermissionName = 'nonBlocked' ) : boolean {
        const permission = 'insert';

        let retVal : boolean = false;

        if (
            user
            && itemTypeName
            && user.hasPermissionOnItemType( permission, itemTypeName, schema )
            && (
                user.hasGenericPermissionOnItemType( genericPermission, itemTypeName, schema )
                || (
                    ( schema === 'CFG' && itemTypeName === 'Group' )
                    && ( !!global.user?.isMemberOfGroup( config.sysGroups?.groupAdmin ?? '' ) || !!global.user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) )
                )
                || (
                    ( schema === 'CFG' && itemTypeName === 'User' )
                    && ( !!global.user?.isMemberOfGroup( config.sysGroups?.userAdmin ?? '' ) || !!global.user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) )
                )
                || (
                    ( schema === 'CFG' && itemTypeName === 'YnItemType' )
                    && ( !!global.user?.isMemberOfGroup( config.sysGroups?.metaModelAdmin ?? '' ) || !!global.user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) )
                )
            )
        ) {
            retVal = true;
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  className,
                  columnDefinitions,
                  columns,
                  dataContext,
                  dataStructure,
                  editMode,
                  editable,
                  rawMode,
                  saveButtonErrorCount,
                  saveButtonErrorTooltip,
                  schema,
                  searchString,
                  showAddButton,
                  showAdditionalRawNames,
                  showClearSearchButton,
                  showColumnOptionsButton,
                  showColumnSortButton,
                  showDownloadXlsxButton,
                  showModifiedHint,
                  showPropertyDefaults,
                  showReloadButton,
                  showSaveButton,
                  showSearchButton,
                  showSearchField,
                  showViewOptionsButton,
                  showViewSelector,
                  sortable,
                  sorters,
                  uid,
                  user,
                  viewKey,
                  viewSelectorAsConfiguredPanelProps,
                  viewSelectorPanelProps
              }                                          = this.props,
              layoutComponents : ToolbarLayoutComponents = {
                  left : [],
                  right: []
              };

        if ( dataStructure ) {

            // left
            // show save button only if overview is editable
            if ( showSaveButton && editable ) {
                layoutComponents.left.push( {
                    type  : 'saveButton',
                    saveButtonErrorCount,
                    saveButtonErrorTooltip,
                    params: {
                        key: `${uid}_saveButton`,

                        className: 'yn-responsive-hidden',
                        disabled : !editMode,
                        onClick  : this.onSaveButtonClick
                    }
                } );
            }

            if ( showAddButton ) {
                const rootDataObjectSchema   = ( dataStructure?.item?.objectSchema ?? 'DATA' ).toUpperCase() as Schema,
                      rootDataObjectItemType = dataStructure?.item?.objectType;

                let addButtonDisabled : boolean = true;

                if ( rootDataObjectSchema && rootDataObjectItemType ) {
                    addButtonDisabled = !(
                        this.isInsertable( rootDataObjectSchema, rootDataObjectItemType, user )
                        || (
                            this.isInsertable( rootDataObjectSchema, rootDataObjectItemType, user, 'none' )
                            && !!user?.getPanelPropsForItemType( rootDataObjectSchema, rootDataObjectItemType )?.length
                        )
                    );
                }

                layoutComponents.left.push( {
                    type  : 'addButton',
                    params: {
                        key: `${uid}_addButton`,

                        disabled: addButtonDisabled,
                        onClick : this.onAddItemButtonClick
                    }
                } );
            }

            layoutComponents.left.push( {
                type  : 'separator',
                params: {
                    className: 'yn-responsive-hidden'
                }
            } );

            if ( showDownloadXlsxButton ) {
                layoutComponents.left.push( {
                    type  : 'downloadXlsx',
                    params: {
                        key: `${uid}_downloadXlsxButton`,

                        className: 'yn-responsive-hidden',
                        onClick  : () => {
                            void this.onDownloadXlsxClick();
                        }
                    }
                } );
            }

            layoutComponents.left.push( {
                type  : 'separator',
                params: {
                    className: 'yn-responsive-hidden'
                }
            } );

            if ( showSearchField ) {
                layoutComponents.left.push( {
                    type  : 'searchField',
                    params: {
                        key: `${uid}_searchField`,

                        uid,

                        value    : searchString,
                        markDirty: true,
                        onChange : this.onSearchFieldChange,
                        onClear  : this.onSearchFieldClear,
                        onSearch : this.onSearchFieldSearch,

                        label     : __( 'YN.base.finder.searchString.label' ),
                        inputStyle: { width: '18rem' }
                    }
                } );
            }
            else if ( showSearchButton ) {
                layoutComponents.left.push( {
                    type  : 'searchButton',
                    params: {
                        key: `${uid}_searchButton`,

                        className: 'yn-responsive-hidden',
                        onClick  : this.onReloadButtonClick.bind( this, false )
                    }
                } );
            }

            layoutComponents.left.push( {
                type  : 'separator',
                params: {
                    className: 'yn-responsive-hidden'
                }
            } );

            if ( showClearSearchButton ) {
                layoutComponents.left.push( {
                    type  : 'clearSearchButton',
                    params: {
                        key: `${uid}_clearSearchButton`,

                        className: 'yn-responsive-hidden',
                        onClick  : this.onClearSearchButtonClick
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showModifiedHint ) {
                layoutComponents.left.push( {
                    type  : 'custom',
                    params: {
                        key      : `${uid}_modifiedHint`,
                        className: 'flex align-items-center',
                        component: <>
                            <div className="yn-toolbar-modified-hint-icon"/>
                            <span className="ml-2 yn-secondary">{__( 'YN.base.frontend.common.modified.hint' )}</span>
                        </>
                    }
                } );
            }


            // right
            if ( showViewSelector ) {
                const viewOptions : ViewOption[] = [
                    {
                        key  : 'forceFit',
                        type : 'BOOLEAN',
                        label: __( 'YN.base.frontend.dataTable.viewSelector.option.forceFit.label' )
                    }
                ];

                if ( editable ) {
                    viewOptions.unshift( {
                        key  : 'editMode',
                        type : 'BOOLEAN',
                        label: __( 'YN.base.frontend.dataTable.viewSelector.option.editMode.label' )
                    } );
                }

                layoutComponents.right.push( {
                    type  : 'viewSelectorDropdown',
                    params: {
                        key: `${uid}_viewSelectorDropdown`,

                        panelKey: uid,

                        module                : `${Overview.viewSelectorModule}${user?.viewConfigSeparator}${viewKey}`,
                        views                 : this.getViewsForViewSelectorDropdown( viewKey ),
                        selectedView          : this.getSelectedViewForViewSelectorDropdown( viewKey, viewSelectorPanelProps ),
                        asConfiguredPanelProps: viewSelectorAsConfiguredPanelProps,
                        panelProps            : viewSelectorPanelProps,
                        viewOptions,
                        onSetView             : this.onViewSelectorSetView,
                        labeled               : true,
                        className             : 'yn-responsive-hidden',
                        style                 : { width: '18rem' }
                    }
                } );
            }

            // show view options only if no sql or cypher overview
            if ( showViewOptionsButton ) {
                layoutComponents.right.push( {
                    type  : 'viewOptionsMenu',
                    params: {
                        key: `${uid}_viewOptions`,

                        uid: uid,

                        disabled          : dataStructure.id === '-1' || dataStructure.id === '0',
                        options           : [
                            {
                                name    : 'editMode',
                                label   : __( 'YN.base.viewOptions.editMode.label' ),
                                value   : editMode,
                                disabled: !editable
                            },
                            {
                                name : 'showPropertyDefaults',
                                label: __( 'YN.base.viewOptions.propertyDefaults.label' ),
                                value: showPropertyDefaults
                            },
                            {
                                name : 'rawMode',
                                label: __( 'YN.base.viewOptions.rawMode.label' ),
                                value: rawMode
                            },
                            {
                                name    : 'showAdditionalRawNames',
                                label   : __( 'YN.base.viewOptions.additionalRawNames.label' ),
                                value   : showAdditionalRawNames,
                                disabled: rawMode
                            }
                        ],
                        onViewOptionChange: this.onViewOptionChange,

                        className: 'yn-responsive-hidden'
                    }
                } );
            }

            if ( showColumnOptionsButton ) {
                layoutComponents.right.push( {
                    type  : 'columnConfigMenu',
                    params: {
                        key: `${uid}_columnConfig`,

                        uid: uid,

                        columns                     : this.getAllColumns( columnDefinitions, columns, dataStructure, schema, rawMode, showAdditionalRawNames ) as ColumnConfigMenuColumn[],
                        onChange                    : this.onColumnVisibilityChange,
                        onExpandColumnsToHeaderWidth: this.onExpandColumnsToHeaderWidth,
                        onExpandColumnsToScreenWidth: this.onExpandColumnsToScreenWidth,

                        className: 'yn-responsive-hidden'
                    }
                } );
            }

            if ( showColumnSortButton ) {
                layoutComponents.right.push( {
                    type  : 'columnSortMenu',
                    params: {
                        key: `${uid}_columnsort`,

                        uid: uid,

                        disabled: !sortable,
                        columns : this.getAllColumns( columnDefinitions, columns, dataStructure, schema, rawMode, showAdditionalRawNames ),
                        sorters : this.getSortersWithField( sorters ),
                        onChange: this.onColumnSortChange,

                        className: 'yn-responsive-hidden'
                    }
                } );
            }

            layoutComponents.right.push( {
                type  : 'separator',
                params: {
                    className: 'yn-responsive-hidden'
                }
            } );

            if ( showReloadButton ) {
                layoutComponents.right.push( {
                    type  : 'reloadButton',
                    params: {
                        key: `${uid}_reloadButton`,

                        confirm: () => {
                            const modifiedData = getModifiedData( dataContext, schema );

                            return Boolean( modifiedData.items.length || modifiedData.references.length );
                        },
                        onClick: this.onReloadButtonClick.bind( this, true )
                    }
                } );
            }
        }

        const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

        return <PrimeToolbar
            id={`${uid}_toolbar`}
            start={toolbarComponents.left}
            end={toolbarComponents.right}
            className={`${className} yn-panel-toolbar yn-overview-toolbar yn-toolbar-horizontal-separator-bottom`}
            style={{ zIndex: 5 }}
        />;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        elusiveData            : undefined,
        user                   : undefined,
        messageCatalogTimestamp: 0
    };

    if ( ownProps.uid ) {
        // get elusiveData
        if ( state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ];
        }
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    if ( state.messagecatalog?.timestamp ) {
        retVal.messageCatalogTimestamp = state.messagecatalog.timestamp;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( Toolbar );
